import React, { useId, useRef, useState } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { ConfirmDialog } from "../dashboard/ConfirmDialog";

type LogoPickerProps = {
  logo?: string | null;
  label?: string;
  deleteLogo: () => void;
  base64: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const LogoPicker = ({
  logo,
  label = "Logo",
  deleteLogo,
  base64,
}: LogoPickerProps): React.ReactElement => {
  const inputId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

  const handleDelete = () => {
    setDeleteConfirmationOpen(false);
    deleteLogo();
  };

  return (
    <>
      <div className={logo ? "w-full max-w-[404px]" : "w-full"}>
        <input
          ref={fileInputRef}
          id={inputId}
          type="file"
          accept="image/png,image/jpeg,image/jpg"
          className="hidden"
          onChange={base64}
        />

        {!logo ? (
          <label
            htmlFor={inputId}
            className="flex h-24 w-full cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed border-[#888888] bg-white p-3 transition hover:border-cpaGold"
          >
            <FileUploadOutlinedIcon sx={{ fontSize: 24 }} />
            <span className="text-center text-sm font-normal text-[#666666] min-[960px]:text-lg">
              Click to Upload
            </span>
          </label>
        ) : (
          <div className="flex w-full items-center gap-3.5 rounded-lg border border-dashed border-[#888888] bg-white p-3.5 min-[960px]:min-h-[150px] min-[960px]:gap-6 min-[960px]:p-5">
            <div className="flex h-[72px] w-[72px] shrink-0 items-center justify-center overflow-hidden rounded-[5px] border border-[#E8E8E8] bg-white min-[960px]:h-[100px] min-[960px]:w-[100px]">
              <img
                src={logo}
                alt={label}
                className="max-h-full max-w-full object-contain"
              />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-2 min-[960px]:gap-3">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border-0 bg-[#666666] px-3 py-2 text-[13px] font-medium text-white transition hover:bg-[#555555] min-[960px]:min-w-[180px] min-[960px]:gap-2 min-[960px]:px-6 min-[960px]:py-3 min-[960px]:text-base"
              >
                <EditOutlinedIcon className="!h-4 !w-4 min-[960px]:!h-5 min-[960px]:!w-5" />
                Update Logo
              </button>
              <button
                type="button"
                onClick={() => setDeleteConfirmationOpen(true)}
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border-0 bg-[#ED1C24] px-3 py-2 text-[13px] font-medium text-white transition hover:bg-[#D41920] min-[960px]:min-w-[180px] min-[960px]:gap-2 min-[960px]:px-6 min-[960px]:py-3 min-[960px]:text-base"
              >
                <DeleteOutlineIcon className="!h-4 !w-4 min-[960px]:!h-5 min-[960px]:!w-5" />
                Delete Logo
              </button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteConfirmationOpen}
        variant="destructive"
        title={label === "Sponsor Logo" ? "Delete Sponsor Logo" : "Delete Logo"}
        message={
          <>
            <span className="block">
              Are you sure you want to delete this {label.toLowerCase()}?
            </span>
            <span className="mt-2 block">This action cannot be undone.</span>
          </>
        }
        icon={<DeleteOutlineIcon sx={{ fontSize: 56 }} />}
        confirmLabel="DELETE"
        uppercaseActions
        hideCancel
        onConfirm={handleDelete}
        onClose={() => setDeleteConfirmationOpen(false)}
      />
    </>
  );
};

export default LogoPicker;
